package com.eladfinish.bible.contest.extensions

import com.eladfinish.bible.contest.utils.currentAppLanguageCode
import com.eladfinish.bible.contest.utils.toBcp47Language
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.DayOfWeek
import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.minus
import kotlinx.datetime.number
import kotlinx.datetime.todayIn
import kotlin.js.Date
import kotlin.time.Clock
import kotlin.time.ExperimentalTime

actual fun LocalDate.getFormattedDate(pattern: String): String =
    formatByPattern(pattern, year = year, month = month.number, day = day)

actual fun LocalDateTime.getFormattedDate(pattern: String): String =
    formatByPattern(pattern, year = year, month = month.number, day = day, hour = hour, minute = minute, second = second)

/** "today" / "yesterday" / "3 days ago", in the app's language, as the browser words it. */
@OptIn(ExperimentalTime::class)
actual fun LocalDate.getRelativeDay(): String {
    val today = Clock.System.todayIn(TimeZone.currentSystemDefault())
    val daysFromToday = (this.toEpochDays() - today.toEpochDays()).toInt()
    val locale = currentAppLanguageCode.toBcp47Language()
    return js("new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(daysFromToday, 'day')")
        .unsafeCast<String>()
}

actual fun LocalDate.atStartOfWeek(): LocalDate {
    // Week starts on Sunday, matching the app's Hebrew-calendar convention.
    val daysToSubtract = (this.dayOfWeek.isoDayNumber % 7)
    return this.minus(daysToSubtract, DateTimeUnit.DAY)
}

private val DayOfWeek.isoDayNumber: Int
    get() = when (this) {
        DayOfWeek.SUNDAY -> 0
        DayOfWeek.MONDAY -> 1
        DayOfWeek.TUESDAY -> 2
        DayOfWeek.WEDNESDAY -> 3
        DayOfWeek.THURSDAY -> 4
        DayOfWeek.FRIDAY -> 5
        DayOfWeek.SATURDAY -> 6
        else -> 0
    }

/**
 * Formats by a `java.time.format.DateTimeFormatter`-style pattern, which Kotlin/JS doesn't have.
 * Supports the letters y, M, d, E, a, H, h, m, s (the count of a letter sets the width, or picks
 * a short/long name for M and E) and text quoted in single quotes; anything else is copied as is.
 */
private fun formatByPattern(
    pattern: String,
    year: Int,
    month: Int,
    day: Int,
    hour: Int = 0,
    minute: Int = 0,
    second: Int = 0,
): String {
    val locale = currentAppLanguageCode.toBcp47Language()
    val jsDate = Date(year, month - 1, day, hour, minute, second)
    val out = StringBuilder()
    var i = 0
    while (i < pattern.length) {
        val c = pattern[i]
        if (c == '\'') {
            val end = pattern.indexOf('\'', startIndex = i + 1)
            when (end) {
                i + 1 -> out.append('\'')
                -1 -> out.append(pattern, i + 1, pattern.length)
                else -> out.append(pattern, i + 1, end)
            }
            i = if (end == -1) pattern.length else end + 1
            continue
        }

        var count = 1
        while (i + count < pattern.length && pattern[i + count] == c) count++

        fun number(value: Int): String = value.toString().padStart(count, '0')
        fun name(field: String): String {
            val style = if (count >= 4) "long" else "short"
            val options: dynamic = js("({})")
            options[field] = style
            return jsDate.toLocaleDateString(locale, options)
        }

        when (c) {
            'y' -> out.append(if (count == 2) (year % 100).toString().padStart(2, '0') else number(year))
            'M' -> out.append(if (count >= 3) name("month") else number(month))
            'd' -> out.append(number(day))
            'E' -> out.append(name("weekday"))
            'a' -> out.append(if (hour < 12) "AM" else "PM")
            'H' -> out.append(number(hour))
            'h' -> out.append(number(if (hour % 12 == 0) 12 else hour % 12))
            'm' -> out.append(number(minute))
            's' -> out.append(number(second))
            else -> repeat(count) { out.append(c) }
        }
        i += count
    }
    return out.toString()
}
